// Projected weekly hard sets per muscle for a PLANNED program — the prospective analogue of the
// History "weekly sets per muscle" card, computed from the program's workout TARGETS instead of
// logged sets. Pure + client-side so the Program editor can recompute it live on unsaved edits
// (the server can't, since the schedule isn't persisted yet). Mirrors the rules in
// server/routes/coach.ts plannedSignal() / getMuscleSetVolume so the numbers agree with coaching:
//   • resistance metric types only (weight×reps, bodyweight-reps) — cardio/mobility don't build muscle
//   • primary movers count a full set, secondary movers half a set
//   • per-cycle totals are normalized to a 7-day week by the cycle length (program-day count)
// Since these are PLANNED targets, every set counts as "hard" (no RPE filter — that's a logged-data
// concept). Returns muscle_ids; the caller resolves display names (keeps this module store-free).

const RESISTANCE = new Set(["weight_reps", "bodyweight_reps"]);
const round1 = (n) => Math.round(n * 10) / 10;

/**
 * @param days  ordered schedule, one entry per program day: `{ exercises: [...] }`. Rest days
 *              carry no exercises (`{ exercises: [] }` or `null`). Each exercise needs
 *              `metric_type`, `target_sets`, `primary_muscle_ids[]`, `secondary_muscle_ids[]`
 *              (exactly the shape `exercisesForWorkout` returns).
 * @param cycleDays  the cycle length to normalize against; defaults to the schedule length.
 * @returns `{ cycle_days, muscles: [{ muscle_id, sets_per_week, sessions_per_week }] }`,
 *          muscles sorted by descending weekly sets.
 */
export function planVolume(days, cycleDays = days.length) {
  const byMuscle = new Map(); // muscle_id -> { sets, days:Set<dayIndex> }
  const credit = (id, day, sets, weight) => {
    let e = byMuscle.get(id);
    if (!e) { e = { sets: 0, days: new Set() }; byMuscle.set(id, e); }
    e.sets += sets * weight;
    e.days.add(day);
  };

  days.forEach((day, i) => {
    const exercises = (day && day.exercises) || [];
    for (const ex of exercises) {
      if (!RESISTANCE.has(ex.metric_type)) continue;
      const sets = Number(ex.target_sets) || 0;
      if (sets <= 0) continue;
      for (const id of ex.primary_muscle_ids || []) credit(id, i, sets, 1);
      for (const id of ex.secondary_muscle_ids || []) credit(id, i, sets, 0.5);
    }
  });

  const factor = cycleDays > 0 ? 7 / cycleDays : 1; // per-cycle → per-week
  const muscles = [...byMuscle.entries()]
    .map(([muscle_id, e]) => ({
      muscle_id,
      sets_per_week: round1(e.sets * factor),
      sessions_per_week: round1(e.days.size * factor),
    }))
    .sort((a, b) => b.sets_per_week - a.sets_per_week);

  return { cycle_days: cycleDays, muscles };
}
